/**
 * Accept an organisation invitation.
 *
 * Runs unguarded. For a standalone organisation the invitation email carries a
 * passwordless sign-in link (see inviteUser), so the common path is: click the
 * link in the invitation, arrive here already signed in, confirm your name,
 * done. One email, one click.
 *
 * Three states:
 *
 *   - Arriving on the invitation's own sign-in link. The invitation is read
 *     first, because it names the address the link was issued for — which is
 *     exactly what Firebase needs to redeem it, so the invitee never has to
 *     type it. They still confirm it: the link carries both the invitation and
 *     the credential, so a forwarded email would otherwise sign the recipient
 *     straight in as somebody else. One button, and the address is on it.
 *     An expired code falls back to the signed-out state.
 *   - Signed out. The invitation is read through the `getInvitationPreview`
 *     callable, which needs no session, and sign-in is offered on this page.
 *     Nothing navigates away, so the invitation cannot be lost — which is what
 *     used to happen: AuthGuard sent the invitee to a bare login page, the
 *     login page cleared the returnUrl out of the address bar, and a signed-in
 *     user with no organisation then landed on "create an organisation".
 *   - Signed in. The invitation document is read directly (rules allow a `get`
 *     by id to any authenticated user) and can be accepted.
 */

import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { Functions } from '@angular/fire/functions';
import { httpsCallable } from '../../core/services/telemetry';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';

import { AuthenticationService } from '../../core/services/auth.service';
import { roleLabel } from '../../core/models/user-role.model';

/** Shape returned by the `getInvitationPreview` callable. */
interface InvitationPreview {
    valid: boolean;
    reason?: 'not-found' | 'used' | 'expired';
    status?: string;
    orgType?: 'standalone' | 'pco';
    email?: string | null;
    name?: string | null;
    role?: string;
    organizationName?: string | null;
    inviterName?: string | null;
}

@Component({
    selector: 'app-accept-invite',
    standalone: true,
    imports: [CommonModule, RouterLink, FormsModule],
    templateUrl: './accept-invite.component.html',
    styleUrls: ['./accept-invite.component.scss']
})
export class AcceptInviteComponent implements OnInit {
    private auth = inject(Auth);
    private firestore = inject(Firestore);
    private functions = inject(Functions);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private authenticationService = inject(AuthenticationService);

    loading = true;
    error = '';
    invitationData: any = null;
    orgName = '';
    inviterName = '';
    role = '';
    accepting = false;
    /**
     * Required. Nothing in the invite flow ever asked for a name, so a team
     * member who signed up by email had none on their user document — and the
     * campaigns they sent had no sender.
     */
    userName = '';

    /** False until the invitee has an account; drives the sign-in panel. */
    signedIn = false;
    /**
     * Which sign-in options to offer. Planning Center is only shown for a PCO
     * organisation: signing in with it binds the account to whatever
     * organisation the Planning Center claim names, which for a standalone
     * invitation is the wrong one entirely — and, for the first person into
     * that organisation, makes them its owner.
     */
    orgType: 'standalone' | 'pco' = 'standalone';
    /**
     * Arrived on the invitation's sign-in link and waiting on the confirm
     * button. Deliberately not redeemed on load — see the file header.
     */
    pendingSignInLink = false;
    /** The sign-in code in the invitation link had expired or been used. */
    signInLinkExpired = false;
    /** Signed in, but as somebody other than the person invited. */
    wrongAccount = '';
    /** The address the invitation was sent to, used to pre-fill sign-in. */
    inviteEmail = '';
    signingIn = false;
    emailFormOpen = false;
    emailAddress = '';
    emailLinkSent = false;

    private token = '';
    private orgId = '';

    async ngOnInit() {
        this.token = this.route.snapshot.queryParamMap.get('token') ?? '';
        this.orgId = this.route.snapshot.queryParamMap.get('orgId') ?? '';

        if (!this.token || !this.orgId) {
            this.error = 'Invalid invitation link.';
            this.loading = false;
            return;
        }

        const user = await firstValueFrom(authState(this.auth));
        // Present when they signed in with a provider that supplies one; the
        // invitation's own name fills in below if not.
        this.userName = user?.displayName ?? '';

        if (user) {
            await this.loadAsMember();
            this.loading = false;
            return;
        }

        // The invitation itself may be the sign-in link. Read the invitation
        // first: it names the address the link was issued for, which is what
        // Firebase needs to redeem it — so there is nothing to ask the invitee.
        await this.loadAsGuest();

        this.pendingSignInLink = !this.error &&
            !!this.inviteEmail &&
            this.authenticationService.isEmailSignInLink(window.location.href);

        this.loading = false;
    }

    /** Confirm the address on the invitation link and redeem it. */
    async continueAsInvitee(): Promise<void> {
        if (this.signingIn) return;
        this.signingIn = true;

        if (await this.redeemInviteSignInLink()) {
            this.loading = true;
            this.userName = this.auth.currentUser?.displayName || this.userName;
            await this.loadAsMember();
            this.loading = false;
        }

        this.signingIn = false;
    }

    /**
     * Redeem the sign-in code that came with the invitation.
     *
     * The code expires sooner than the invitation does, so failure is an
     * ordinary outcome: the invitation stays on screen and the email panel
     * opens, ready to send a fresh link.
     * @return Whether the invitee is now signed in.
     */
    private async redeemInviteSignInLink(): Promise<boolean> {
        try {
            await this.authenticationService.completeEmailSignIn(this.inviteEmail, window.location.href);

            // Drop the one-time code from the address bar, keeping the
            // invitation params, so a refresh does not retry a spent code.
            this.router.navigate(['/accept-invite'], {
                queryParams: { token: this.token, orgId: this.orgId },
                replaceUrl: true
            });
            return true;
        } catch (err) {
            console.error('Could not redeem the invitation sign-in link:', err);
            this.pendingSignInLink = false;
            this.signInLinkExpired = true;
            this.emailFormOpen = true;
            return false;
        }
    }

    // -------------------------------------------------------------------------
    // Loading
    // -------------------------------------------------------------------------

    /** Signed out: read the invitation through the unauthenticated callable. */
    private async loadAsGuest(): Promise<void> {
        this.signedIn = false;

        try {
            const previewFn = httpsCallable<{ token: string; orgId: string }, InvitationPreview>(
                this.functions, 'getInvitationPreview'
            );
            const preview = (await previewFn({ token: this.token, orgId: this.orgId })).data;

            if (!preview.valid) {
                this.error = this.describeInvalid(preview);
                return;
            }

            this.orgType = preview.orgType === 'pco' ? 'pco' : 'standalone';
            this.orgName = preview.organizationName || 'this organization';
            this.inviterName = preview.inviterName || 'A team member';
            this.role = preview.role || 'viewer';
            this.inviteEmail = preview.email || '';
            this.emailAddress = this.inviteEmail;
            if (!this.userName) {
                this.userName = preview.name || '';
            }

            // Enough for the template to render the invitation; accepting still
            // goes through the authenticated path once they have signed in.
            this.invitationData = { id: this.token, orgId: this.orgId, role: this.role };
        } catch (err) {
            console.error('Error loading invitation preview:', err);
            this.error = 'Failed to load invitation details. Please try again.';
        }
    }

    /** Signed in: read the invitation document directly. */
    private async loadAsMember(): Promise<void> {
        this.signedIn = true;
        this.error = '';
        this.wrongAccount = '';

        try {
            const inviteRef = doc(this.firestore, `organizations/${this.orgId}/invitations/${this.token}`);
            const inviteSnap = await getDoc(inviteRef);

            if (!inviteSnap.exists()) {
                this.error = 'This invitation was not found. It may have been revoked.';
                return;
            }

            const invite = inviteSnap.data();
            this.invitationData = { id: this.token, orgId: this.orgId, ...invite };

            // Planning Center sign-in accepts a Support Staff invitation itself
            // (beforeSignIn admits non-site-admins only that way), so the
            // invitee comes back from the sign-in popup to an invitation that
            // is already theirs. That is success, not "already accepted".
            if (invite['status'] === 'accepted' && invite['acceptedBy']?.uid === this.auth.currentUser?.uid) {
                await this.auth.currentUser?.getIdToken(true);
                this.showJoined(invite['organizationName'] || 'your organization', invite['role'] || 'viewer');
                return;
            }

            // Check if invitation is still valid
            if (invite['status'] !== 'pending') {
                this.error = this.describeStatus(invite['status']);
                return;
            }

            const expiresAt = invite['expiresAt']?.toDate ? invite['expiresAt'].toDate() : new Date(invite['expiresAt']);
            if (expiresAt < new Date()) {
                this.error = 'This invitation has expired. Please ask your administrator to send a new one.';
                return;
            }

            // Check if user already belongs to an org
            const user = this.auth.currentUser!;
            const userRef = doc(this.firestore, `users/${user.uid}`);
            const userSnap = await getDoc(userRef);
            let currentOrgName = '';
            if (userSnap.exists() && userSnap.data()['organizationId'] && userSnap.data()['organizationId'] !== this.orgId) {
                // They are a member of this one, so they can read it.
                currentOrgName = userSnap.data()['organizationName'] || 'another organization';
            }

            // The organisation name comes from the invitation, not the
            // organisation document — the invitee is not a member yet and so
            // cannot read it. Invitations written before this carried no name;
            // they expire within 7 days, so the fallback is short-lived.
            this.orgName = invite['organizationName'] || 'this organization';

            this.inviterName = invite['invitedBy']?.name || invite['invitedBy']?.email || 'A team member';
            this.role = invite['role'] || 'viewer';
            this.inviteEmail = invite['email'] || '';

            // The server refuses a mismatch anyway; saying so here means the
            // invitee is not left pressing Accept and reading a rejection.
            const signedInEmail = user.email?.toLowerCase() ?? '';
            if (signedInEmail && this.inviteEmail && signedInEmail !== this.inviteEmail.toLowerCase()) {
                this.wrongAccount = user.email ?? '';
            }

            // Whoever invited them supplied a name for standalone orgs, so the
            // field is usually already filled in by the time they see it.
            if (!this.userName) {
                this.userName = invite['name'] || '';
            }

            // Show info message if user is currently in another org
            if (currentOrgName) {
                Swal.fire({
                    icon: 'info',
                    title: 'Multi-Organization Access',
                    html: `You are currently logged into <strong>${currentOrgName}</strong>.<br><br>` +
                        `Accepting this invitation will switch you to <strong>${this.orgName}</strong>. ` +
                        `You can switch between organizations from the portal settings.`,
                    confirmButtonColor: '#556ee6',
                    confirmButtonText: 'Continue'
                });
            }
        } catch (err) {
            console.error('Error loading invitation:', err);
            this.error = 'Failed to load invitation details. Please try again.';
        }
    }

    private describeInvalid(preview: InvitationPreview): string {
        switch (preview.reason) {
            case 'expired':
                return 'This invitation has expired. Please ask your administrator to send a new one.';
            case 'used':
                return this.describeStatus(preview.status);
            default:
                return 'This invitation was not found. It may have been revoked.';
        }
    }

    /**
     * Explain an invitation that is no longer pending.
     *
     * A revoked invitation is now the ordinary result of an administrator
     * re-sending one, rather than a sign that something has gone wrong. Saying
     * only "already been revoked" leaves the reader with a dead end when the
     * link they need is sitting in their inbox.
     */
    private describeStatus(status: string | undefined): string {
        if (status === 'revoked') {
            return 'This invitation link has been replaced by a newer one. ' +
                'Please open the most recent invitation email you received.';
        }
        return `This invitation has already been ${status || 'used'}.`;
    }

    // -------------------------------------------------------------------------
    // Sign-in, in place
    //
    // Both providers use a popup, so the page survives and the invitation is
    // still on screen afterwards — no returnUrl to carry and lose.
    // -------------------------------------------------------------------------

    async signInWithMicrosoft(): Promise<void> {
        this.signingIn = true;
        try {
            const user = await firstValueFrom(this.authenticationService.loginWithMicrosoft());
            if (user) await this.afterSignIn();
        } catch (err) {
            this.showSignInError(err);
        }
        this.signingIn = false;
    }

    async signInWithPlanningCenter(): Promise<void> {
        this.signingIn = true;
        try {
            const user = await firstValueFrom(this.authenticationService.loginWithOIDC());
            if (user) await this.afterSignIn();
        } catch (err) {
            this.showSignInError(err);
        }
        this.signingIn = false;
    }

    /**
     * Email is the one path that leaves the page: Firebase's magic link is a
     * two-leg flow. The invitation URL is carried as the returnUrl so the
     * second leg comes back here.
     */
    async sendEmailLink(): Promise<void> {
        const email = this.emailAddress.trim();
        if (!email) {
            Swal.fire({ icon: 'warning', title: 'Enter your email address' });
            return;
        }

        this.signingIn = true;
        try {
            await this.authenticationService.sendEmailSignInLink(
                email,
                `/accept-invite?token=${this.token}&orgId=${this.orgId}`
            );
            this.emailLinkSent = true;
        } catch (err) {
            this.showSignInError(err);
        }
        this.signingIn = false;
    }

    private async afterSignIn(): Promise<void> {
        this.loading = true;
        this.userName = this.auth.currentUser?.displayName || this.userName;
        await this.loadAsMember();
        this.loading = false;
    }

    /** Decline the address on the link and fall back to the usual options. */
    useAnotherAccount(): void {
        this.pendingSignInLink = false;
    }

    /** Sign out and go back to the sign-in panel, still on the invitation. */
    async switchAccount(): Promise<void> {
        await this.authenticationService.logout();
        this.signedIn = false;
        this.wrongAccount = '';
        this.error = '';
        this.emailFormOpen = false;
        this.emailLinkSent = false;
        this.emailAddress = this.inviteEmail;
    }

    private showSignInError(err: unknown): void {
        const code = (err as { code?: string })?.code || '';
        if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
            return;
        }
        Swal.fire({
            icon: 'error',
            title: 'Could not sign you in',
            text: (err as { message?: string })?.message || 'Please try again.'
        });
    }

    // -------------------------------------------------------------------------
    // Accepting
    // -------------------------------------------------------------------------

    async acceptInvitation() {
        if (!this.invitationData || this.accepting) return;

        this.accepting = true;
        const user = this.auth.currentUser;
        if (!user) {
            this.error = 'You must be signed in to accept an invitation.';
            this.accepting = false;
            return;
        }

        const { id: token, orgId } = this.invitationData;

        const userName = this.userName.trim();
        if (!userName) {
            Swal.fire({
                icon: 'warning',
                title: 'What should we call you?',
                text: 'Your name appears on messages you send, so recipients know who they are talking to.'
            });
            this.accepting = false;
            return;
        }

        try {
            const acceptFn = httpsCallable<
                { token: string; orgId: string; userName: string },
                { success: boolean; orgName: string; role: string }
            >(this.functions, 'acceptInvitation');

            const result = await acceptFn({ token, orgId, userName });

            // Force token refresh to pick up new custom claims
            await user.getIdToken(true);

            this.showJoined(result.data.orgName, result.data.role);
        } catch (err: any) {
            console.error('Error accepting invitation:', err);
            this.error = err.message || 'Failed to accept invitation. Please try again.';
        }

        this.accepting = false;
    }

    /**
     * Welcome the new member and take them in.
     *
     * Support Staff only have Conversations; conversationsOnlyGuard would
     * bounce /dashboard there anyway, but the button should say where it is going.
     */
    private showJoined(orgName: string, role: string): void {
        const isSupport = role === 'support';
        const joinedAs = isSupport ? 'Support Staff' : `${role === 'administrator' ? 'an' : 'a'} ${role}`;

        Swal.fire({
            icon: 'success',
            title: 'Welcome!',
            text: `You've joined ${orgName} as ${joinedAs}.`,
            confirmButtonColor: '#556ee6',
            confirmButtonText: isSupport ? 'Go to Conversations' : 'Go to Dashboard'
        }).then(() => {
            this.router.navigate([isSupport ? '/conversations' : '/dashboard']);
        });
    }

    getRoleDisplayName(role: string): string {
        return roleLabel(role);
    }
}
